import { c as _c } from "react/compiler-runtime";
import React, { useContext, useEffect, useRef, useState } from "react";
import { BarChart3, BookOpen, LifeBuoy, LogOut, Monitor, Moon, Palette, SettingsIcon, Sun, Wrench } from "lucide-react";
import { useTheme } from "next-themes";
import posthog from "posthog-js";
import { AuthContext } from "../AuthProvider";
import { NotificationFloatingButton } from "../NotificationFloatingButton";
import { StatusWidgetMorph, RailStatusHoverCard } from "./StatusIndicator";
import { BSErrorBoundary } from "../ui/bs-error-boundary";
import { DropdownMenu, DropdownMenuContent, DropdownMenuControlOption, DropdownMenuControlRow, DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuSeparator, DropdownMenuTrigger } from "@repo/blacksmith-ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { Button } from "@repo/blacksmith-ui/button";
import { RoutePrefetchLink } from "@/components/ui/route-prefetch-link";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { useSetPersistedTheme } from "@/hooks/useAppearanceSettings";
import { useRoutePrefetchIntent } from "@/hooks/useRoutePrefetchIntent";
import { useIsHydrated } from "@/components/codesmith/useIsHydrated";
import { cn } from "@repo/blacksmith-ui/cn";
import { RAIL_REDESIGN_WIDTH, RAIL_WIDTH } from "./railConstants";
export type SidebarActiveSection = "home" | "actions" | "codesmith" | "usage" | "admin";
export const RAIL_ROW = cn("rail-row flex h-8 w-full items-center overflow-hidden rounded-md cursor-pointer transition-none", "type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950", "[&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900", "dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)]");
export const RAIL_ROW_ACTIVE = "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white";
export const RAIL_REDESIGN_CLASS = "[&_.rail-row]:rounded-[4px] [&_.rail-row-icon]:w-full [&_.rail-row-icon>svg]:size-4";
export function RailRowContents(t0) {
  const $ = _c(15);
  const {
    icon,
    label,
    badge,
    expanded: t1,
    hideLabelWhenCollapsed: t2
  } = t0;
  const expanded = t1 === undefined ? true : t1;
  const hideLabelWhenCollapsed = t2 === undefined ? true : t2;
  const collapsedAndRemoved = hideLabelWhenCollapsed && !expanded;
  let t3;
  if ($[0] !== icon) {
    t3 = <span className="rail-row-icon flex size-8 shrink-0 items-center justify-center">{icon}</span>;
    $[0] = icon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = collapsedAndRemoved && "hidden";
  const t5 = !badge && "pr-2";
  let t6;
  if ($[2] !== t4 || $[3] !== t5) {
    t6 = cn("whitespace-nowrap text-left", t4, t5);
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== label || $[6] !== t6) {
    t7 = <span className={t6}>{label}</span>;
    $[5] = label;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== badge || $[9] !== collapsedAndRemoved) {
    t8 = badge && !collapsedAndRemoved ? <span className="ml-auto mr-2 shrink-0">{badge}</span> : null;
    $[8] = badge;
    $[9] = collapsedAndRemoved;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t3 || $[12] !== t7 || $[13] !== t8) {
    t9 = <>{t3}{t7}{t8}</>;
    $[11] = t3;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
}
const THEME_OPTIONS = [{
  value: "system",
  label: "System",
  icon: Monitor
}, {
  value: "light",
  label: "Light",
  icon: Sun
}, {
  value: "dark",
  label: "Dark",
  icon: Moon
}] as const;
export function AppearanceThemeToggle() {
  const $ = _c(4);
  const {
    theme
  } = useTheme();
  const setTheme = useSetPersistedTheme();
  const mounted = useIsHydrated();
  const activeTheme = mounted ? theme ?? "system" : "system";
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = THEME_OPTIONS.map(_temp);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== activeTheme || $[2] !== setTheme) {
    t1 = <DropdownMenuRadioGroup value={activeTheme} onValueChange={setTheme} className="-mr-1 flex items-center gap-0.5">{t0}</DropdownMenuRadioGroup>;
    $[1] = activeTheme;
    $[2] = setTheme;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

// A right-anchored label tooltip for a collapsed rail/sidebar row. Used both by
// the icon-collapsible sidebar and by the slim app-switcher rail (which no
// longer hover-expands to reveal labels, so each row surfaces its label here).
function _temp(t0) {
  const {
    value,
    label,
    icon: Icon
  } = t0;
  return <DropdownMenuControlOption key={value} value={value} aria-label={label} textValue={label}><Icon /></DropdownMenuControlOption>;
}
export function CollapsedIconTooltip(t0) {
  const $ = _c(8);
  const {
    label,
    enabled,
    shortcut,
    children
  } = t0;
  if (!enabled) {
    return children;
  }
  let t1;
  if ($[0] !== children) {
    t1 = <TooltipTrigger asChild={true}>{children}</TooltipTrigger>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label || $[3] !== shortcut) {
    t2 = shortcut ? <TooltipContent side="right" className="flex items-center gap-2"><span className="leading-none">{label}</span><ShortcutKeys keys={[shortcut]} /></TooltipContent> : <TooltipContent side="right">{label}</TooltipContent>;
    $[2] = label;
    $[3] = shortcut;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <Tooltip>{t1}{t2}</Tooltip>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function useSidebarLogout() {
  const $ = _c(2);
  const {
    logout
  } = useContext(AuthContext);
  let t0;
  if ($[0] !== logout) {
    t0 = () => {
      posthog?.reset();
      logout();
    };
    $[0] = logout;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function SidebarAdminRow(t0) {
  const $ = _c(19);
  const {
    expanded,
    active,
    onClick,
    hideLabelWhenCollapsed: t1,
    divider: t2
  } = t0;
  const hideLabelWhenCollapsed = t1 === undefined ? true : t1;
  const divider = t2 === undefined ? true : t2;
  const {
    user
  } = useContext(AuthContext);
  if (!user?.is_superuser) {
    return null;
  }
  let t3;
  if ($[0] !== divider) {
    t3 = divider && <div className="my-2"><hr className="border-[#0c0c0d14] dark:border-white/12" /></div>;
    $[0] = divider;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = hideLabelWhenCollapsed && !expanded;
  const t5 = active ? "page" : undefined;
  const t6 = active && RAIL_ROW_ACTIVE;
  let t7;
  if ($[2] !== t6) {
    t7 = cn(RAIL_ROW, t6, "group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:justify-center");
    $[2] = t6;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Wrench className="size-[18px]" />;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  let t9;
  if ($[5] !== expanded || $[6] !== hideLabelWhenCollapsed) {
    t9 = <RailRowContents icon={t8} label="Admin" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[5] = expanded;
    $[6] = hideLabelWhenCollapsed;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== onClick || $[9] !== t5 || $[10] !== t7 || $[11] !== t9) {
    t10 = <RoutePrefetchLink href="/admin" onClick={onClick} aria-label="Admin" aria-current={t5} className={t7}>{t9}</RoutePrefetchLink>;
    $[8] = onClick;
    $[9] = t5;
    $[10] = t7;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t4) {
    t11 = <CollapsedIconTooltip label="Admin" enabled={t4}>{t10}</CollapsedIconTooltip>;
    $[13] = t10;
    $[14] = t4;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t3) {
    t12 = <>{t3}{t11}</>;
    $[16] = t11;
    $[17] = t3;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  return t12;
}
export function SidebarGlobalFooter(t0) {
  const $ = _c(89);
  const {
    expanded,
    currentOrg,
    settingsHref,
    activeSection,
    onSectionClick,
    className,
    hideLabelWhenCollapsed: t1,
    statusAsHoverCard: t2,
    redesign: t3,
    leading
  } = t0;
  const hideLabelWhenCollapsed = t1 === undefined ? true : t1;
  const statusAsHoverCard = t2 === undefined ? false : t2;
  const redesign = t3 === undefined ? false : t3;
  const {
    user
  } = useContext(AuthContext);
  const handleLogout = useSidebarLogout();
  const usageHref = currentOrg ? `/${currentOrg}/usage` : "/usage";
  const supportHref = currentOrg ? `/${currentOrg}/support` : "/support";
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const settingsTriggerRef = useRef(null);
  const settingsPrefetchIntent = useRoutePrefetchIntent(currentOrg ? settingsHref : null);
  const settingsMenuCloseTimer = useRef(null);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (settingsMenuCloseTimer.current) {
        clearTimeout(settingsMenuCloseTimer.current);
        settingsMenuCloseTimer.current = null;
      }
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const cancelSettingsMenuClose = t4;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = event => {
      if (event.pointerType === "touch") {
        return;
      }
      cancelSettingsMenuClose();
      setSettingsMenuOpen(true);
    };
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const openSettingsMenu = t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event_0 => {
      if (event_0.pointerType === "touch") {
        return;
      }
      cancelSettingsMenuClose();
      settingsMenuCloseTimer.current = setTimeout(() => setSettingsMenuOpen(false), 120);
    };
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const scheduleSettingsMenuClose = t6;
  let t7;
  let t8;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => cancelSettingsMenuClose;
    t8 = [];
    $[3] = t7;
    $[4] = t8;
  } else {
    t7 = $[3];
    t8 = $[4];
  }
  useEffect(t7, t8);
  let t9;
  if ($[5] !== expanded || $[6] !== hideLabelWhenCollapsed) {
    t9 = <NotificationFloatingButton variant="sidebar" label="Notifications" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[5] = expanded;
    $[6] = hideLabelWhenCollapsed;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const notifications = t9;
  const t10 = redesign ? undefined : "pt-1";
  let t11;
  if ($[8] !== expanded || $[9] !== redesign || $[10] !== statusAsHoverCard) {
    t11 = statusAsHoverCard ? <RailStatusHoverCard railWidth={redesign ? RAIL_REDESIGN_WIDTH : RAIL_WIDTH} /> : <StatusWidgetMorph expanded={expanded} />;
    $[8] = expanded;
    $[9] = redesign;
    $[10] = statusAsHoverCard;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== t10 || $[13] !== t11) {
    t12 = <BSErrorBoundary title="Status Indicator" fallback={null}><div className={t10}>{t11}</div></BSErrorBoundary>;
    $[12] = t10;
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const status = t12;
  let t13;
  if ($[15] !== settingsMenuOpen) {
    t13 = event_1 => {
      if (settingsMenuOpen && event_1.pointerType !== "touch" && event_1.button === 0 && !event_1.ctrlKey) {
        event_1.preventDefault();
      }
    };
    $[15] = settingsMenuOpen;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SettingsIcon className="size-[18px]" />;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== expanded || $[19] !== hideLabelWhenCollapsed) {
    t15 = <RailRowContents icon={t14} label="Settings" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[18] = expanded;
    $[19] = hideLabelWhenCollapsed;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== settingsPrefetchIntent.onFocus || $[22] !== settingsPrefetchIntent.onMouseEnter || $[23] !== t13 || $[24] !== t15) {
    t16 = <DropdownMenuTrigger asChild={true}><button ref={settingsTriggerRef} type="button" aria-label="Settings menu" className={RAIL_ROW} onPointerEnter={openSettingsMenu} onPointerLeave={scheduleSettingsMenuClose} onMouseEnter={settingsPrefetchIntent.onMouseEnter} onFocus={settingsPrefetchIntent.onFocus} onPointerDown={t13}>{t15}</button></DropdownMenuTrigger>;
    $[21] = settingsPrefetchIntent.onFocus;
    $[22] = settingsPrefetchIntent.onMouseEnter;
    $[23] = t13;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = event_3 => {
      const target = event_3.target;
      if (target instanceof Node && settingsTriggerRef.current?.contains(target)) {
        event_3.preventDefault();
      }
    };
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <SettingsIcon />;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  const t19 = !!currentOrg;
  let t20;
  if ($[28] !== settingsHref || $[29] !== t19) {
    t20 = <DropdownMenuItem asChild={true} icon={t18}><RoutePrefetchLink href={settingsHref} prefetchOnIntent={t19}>Settings</RoutePrefetchLink></DropdownMenuItem>;
    $[28] = settingsHref;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <LifeBuoy />;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  const t22 = !!currentOrg;
  let t23;
  if ($[32] !== supportHref || $[33] !== t22) {
    t23 = <DropdownMenuItem asChild={true} icon={t21}><RoutePrefetchLink href={supportHref} prefetchOnIntent={t22}>Contact Support</RoutePrefetchLink></DropdownMenuItem>;
    $[32] = supportHref;
    $[33] = t22;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  let t24;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <DropdownMenuItem asChild={true} icon={<BookOpen />}><a href="https://docs.blacksmith.sh" target="_blank" rel="noopener noreferrer">Documentation...</a></DropdownMenuItem>;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Palette />;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  let t27;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <DropdownMenuControlRow icon={t25} label="Appearance"><AppearanceThemeToggle /></DropdownMenuControlRow>;
    t27 = <DropdownMenuSeparator />;
    $[37] = t26;
    $[38] = t27;
  } else {
    t26 = $[37];
    t27 = $[38];
  }
  let t28;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <LogOut />;
    $[39] = t28;
  } else {
    t28 = $[39];
  }
  let t29;
  if ($[40] !== user) {
    t29 = user?.username ? <span className="block max-w-[200px] truncate" title={user.username}>{user.username}</span> : undefined;
    $[40] = user;
    $[41] = t29;
  } else {
    t29 = $[41];
  }
  let t30;
  if ($[42] !== handleLogout || $[43] !== t29) {
    t30 = <DropdownMenuItem icon={t28} trailing={t29} onClick={handleLogout}>Log out</DropdownMenuItem>;
    $[42] = handleLogout;
    $[43] = t29;
    $[44] = t30;
  } else {
    t30 = $[44];
  }
  let t31;
  if ($[45] !== t20 || $[46] !== t23 || $[47] !== t30) {
    t31 = <DropdownMenuContent side="right" align="start" sideOffset={8} alignOffset={-31} className="min-w-[216px] max-w-[320px]" onPointerEnter={openSettingsMenu} onPointerLeave={scheduleSettingsMenuClose} onCloseAutoFocus={_temp2} onPointerDownOutside={t17}>{t20}{t23}{t24}{t26}{t27}{t30}</DropdownMenuContent>;
    $[45] = t20;
    $[46] = t23;
    $[47] = t30;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  let t32;
  if ($[49] !== settingsMenuOpen || $[50] !== t16 || $[51] !== t31) {
    t32 = <DropdownMenu modal={false} open={settingsMenuOpen} onOpenChange={setSettingsMenuOpen}>{t16}{t31}</DropdownMenu>;
    $[49] = settingsMenuOpen;
    $[50] = t16;
    $[51] = t31;
    $[52] = t32;
  } else {
    t32 = $[52];
  }
  const settingsMenu = t32;
  const t33 = hideLabelWhenCollapsed && !expanded;
  const t34 = currentOrg ? true : null;
  const t35 = !!currentOrg;
  let t36;
  if ($[53] !== onSectionClick) {
    t36 = onSectionClick?.("usage");
    $[53] = onSectionClick;
    $[54] = t36;
  } else {
    t36 = $[54];
  }
  const t37 = activeSection === "usage" ? "page" : undefined;
  const t38 = activeSection === "usage" && RAIL_ROW_ACTIVE;
  let t39;
  if ($[55] !== t38) {
    t39 = cn(RAIL_ROW, t38);
    $[55] = t38;
    $[56] = t39;
  } else {
    t39 = $[56];
  }
  let t40;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <BarChart3 className="size-[18px]" />;
    $[57] = t40;
  } else {
    t40 = $[57];
  }
  let t41;
  if ($[58] !== expanded || $[59] !== hideLabelWhenCollapsed) {
    t41 = <RailRowContents icon={t40} label="Usage and Billing" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[58] = expanded;
    $[59] = hideLabelWhenCollapsed;
    $[60] = t41;
  } else {
    t41 = $[60];
  }
  let t42;
  if ($[61] !== t34 || $[62] !== t35 || $[63] !== t36 || $[64] !== t37 || $[65] !== t39 || $[66] !== t41 || $[67] !== usageHref) {
    t42 = <RoutePrefetchLink href={usageHref} prefetch={t34} prefetchOnIntent={t35} onClick={t36} aria-label="Usage and Billing" aria-current={t37} className={t39}>{t41}</RoutePrefetchLink>;
    $[61] = t34;
    $[62] = t35;
    $[63] = t36;
    $[64] = t37;
    $[65] = t39;
    $[66] = t41;
    $[67] = usageHref;
    $[68] = t42;
  } else {
    t42 = $[68];
  }
  let t43;
  if ($[69] !== t33 || $[70] !== t42) {
    t43 = <CollapsedIconTooltip label="Usage and Billing" enabled={t33}>{t42}</CollapsedIconTooltip>;
    $[69] = t33;
    $[70] = t42;
    $[71] = t43;
  } else {
    t43 = $[71];
  }
  const usage = t43;
  if (redesign) {
    let t44;
    if ($[72] !== className) {
      t44 = cn("rail-rows flex flex-col", className);
      $[72] = className;
      $[73] = t44;
    } else {
      t44 = $[73];
    }
    let t45;
    if ($[74] !== leading || $[75] !== notifications || $[76] !== settingsMenu || $[77] !== status || $[78] !== t44 || $[79] !== usage) {
      t45 = <div className={t44}>{leading}{usage}{notifications}{settingsMenu}{status}</div>;
      $[74] = leading;
      $[75] = notifications;
      $[76] = settingsMenu;
      $[77] = status;
      $[78] = t44;
      $[79] = usage;
      $[80] = t45;
    } else {
      t45 = $[80];
    }
    return t45;
  }
  let t44;
  if ($[81] !== className) {
    t44 = cn("rail-rows flex flex-col gap-1", className);
    $[81] = className;
    $[82] = t44;
  } else {
    t44 = $[82];
  }
  let t45;
  if ($[83] !== notifications || $[84] !== settingsMenu || $[85] !== status || $[86] !== t44 || $[87] !== usage) {
    t45 = <div className={t44}>{usage}{settingsMenu}{notifications}{status}</div>;
    $[83] = notifications;
    $[84] = settingsMenu;
    $[85] = status;
    $[86] = t44;
    $[87] = usage;
    $[88] = t45;
  } else {
    t45 = $[88];
  }
  return t45;
}
function _temp2(event_2) {
  return event_2.preventDefault();
}